import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type CustomAdventure } from '@/utils/businessAppApi';
import { SimpleDateSelection } from './components/SimpleDateSelection';
import { SimpleAdventureOptions } from './components/SimpleAdventureOptions';
import { SimpleCustomerDetails } from './components/SimpleCustomerDetails';
import { BookingProgress } from './components/BookingProgress';
import { PublicPriceCalculator } from './components/PublicPriceCalculator';

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  specialRequests?: string;
  preferredContactMethod: 'email' | 'phone' | 'whatsapp';
}

interface BookingRequest {
  selectedOptions: Record<string, unknown>;
  totalPrice: number;
  dateRange: DateRange;
  customerDetails: CustomerDetails;
  flexibility: 'exact' | 'flexible_1_week' | 'flexible_2_weeks' | 'flexible_1_month';
}

interface SuperSimpleFormProps {
  adventure: CustomAdventure;
  onSubmit: (bookingRequest: BookingRequest) => void;
  adventureOptions?: Record<string, any>;
  onAdventureOptionsChange?: (options: Record<string, any>) => void;
  onDateRangeChange?: (dateRange: DateRange) => void;
  /** Calculated total price from PublicPriceCalculator - used on submit instead of raw basePrice */
  currentTotalPrice?: number;
  /** Price breakdown from PublicPriceCalculator for confirmation page and email */
  currentPriceBreakdown?: Array<{ id?: string; label: string; description?: string; amount: number; type?: 'base' | 'option' }>;
}

export function SuperSimpleForm({ 
  adventure, 
  onSubmit, 
  adventureOptions: externalAdventureOptions, 
  onAdventureOptionsChange,
  onDateRangeChange,
  currentTotalPrice,
  currentPriceBreakdown
}: SuperSimpleFormProps) {
  // DEBUG: Track component lifecycle (key warning indicator)
  useEffect(() => {
    console.log('🚀 [SuperSimpleForm] MOUNTED:', adventure.name);
    
    return () => {
      console.log('💥 [SuperSimpleForm] UNMOUNTED:', adventure.name, '← WARNING: This should NOT happen during normal form interaction!');
    };
  }, [adventure.id, adventure.name]);

  // Form state - these are completely independent and should never reset
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [flexibility, setFlexibility] = useState<string>('exact');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [contactMethod, setContactMethod] = useState('email');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // DEBUG: Log date changes and notify parent
  useEffect(() => {
    const hasValidRange = dateRange.from && dateRange.to;
    console.log(`🗓️ [SuperSimpleForm] Dates: ${hasValidRange ? 'SET' : 'EMPTY'}`, 
      hasValidRange ? `${dateRange.from?.toDateString()} - ${dateRange.to?.toDateString()}` : 'No dates selected');
    
    // Notify parent of date range changes for price calculator
    if (onDateRangeChange) {
      onDateRangeChange(dateRange);
    }
  }, [dateRange, adventure.id, adventure.name, onDateRangeChange]);

  // Adventure options state - controlled by parent if provided
  const [internalAdventureOptions, setInternalAdventureOptions] = useState<Record<string, any>>({});
  
  // Use external state if provided, otherwise use internal state
  const adventureOptions = externalAdventureOptions ?? internalAdventureOptions;
  const updateAdventureOptions = onAdventureOptionsChange ?? setInternalAdventureOptions;

  useEffect(() => {
    console.log('🎯 [SuperSimpleForm] Adventure options changed:', {
      adventureOptions,
      externalAdventureOptions,
      adventureId: adventure.id
    });
  }, [adventureOptions, externalAdventureOptions, adventure.id]);

  useEffect(() => {
    console.log('🏔️ [SuperSimpleForm] Adventure changed - potential re-mount:', {
      adventureId: adventure.id,
      adventureName: adventure.name,
      currentDateRange: dateRange
    });
  }, [adventure.id, adventure.name]);

  // Prevent external state changes from affecting form state
  useEffect(() => {
    // This effect intentionally does nothing - it just ensures that
    // if the parent component re-renders or adventure changes,
    // our form state (dates, customer details) remains stable
  }, [adventure.id]); // Only depend on adventure ID, not the whole object

  // Calculate current step based on form completion - more progressive logic
  const currentStep = useMemo(() => {
    console.log('📊 [SuperSimpleForm] Calculating current step:', {
      dateRange,
      adventureOptions,
      adventureId: adventure.id,
      customerName,
      customerEmail,
      customerPhone
    });

    // Step 1: Date selection (must have both dates)
    if (!dateRange.from || !dateRange.to) {
      console.log('📊 [SuperSimpleForm] Current step: 1 (missing dates)');
      return 1;
    }
    
    // Step 2: Adventure customization (for required options only)
    // Filter out days options since they come from calendar
    // Guard against undefined options during loading
    if (!adventure.options || !Array.isArray(adventure.options)) {
      console.log('📊 [SuperSimpleForm] Current step: 1 (adventure options not loaded yet)');
      return 1;
    }
    
    const filteredOptions = adventure.options.filter(option => {
      const lowerLabel = option.label.toLowerCase();
      const isDaysOption = lowerLabel.includes('day') || 
                          lowerLabel.includes('days') || 
                          lowerLabel.includes('night') || 
                          lowerLabel.includes('nights');
      return !isDaysOption;
    });
    const requiredOptions = filteredOptions.filter(option => option.required);
    console.log('📊 [SuperSimpleForm] Required options:', requiredOptions.map(opt => opt.label));
    
    if (requiredOptions.length > 0) {
      const hasAllRequiredOptions = requiredOptions.every(option => {
        const value = adventureOptions[option.label];
        const isValid = value !== undefined && value !== null && value !== '' && value !== false;
        console.log(`📊 [SuperSimpleForm] Option "${option.label}": value=${value}, isValid=${isValid}`);
        return isValid;
      });
      
      if (!hasAllRequiredOptions) {
        console.log('📊 [SuperSimpleForm] Current step: 2 (missing required options)');
        return 2;
      }
    } else {
      // If no required options, skip to step 3 after date selection
      if (!customerName || !customerEmail || !customerPhone) {
        console.log('📊 [SuperSimpleForm] Current step: 3 (no required options, missing customer details)');
        return 3;
      }
      console.log('📊 [SuperSimpleForm] Current step: 4 (no required options, all complete)');
      return 4;
    }
    
    // Step 3: Customer details
    if (!customerName || !customerEmail || !customerPhone) {
      console.log('📊 [SuperSimpleForm] Current step: 3 (missing customer details)');
      return 3;
    }
    
    // Step 4: Ready to submit
    console.log('📊 [SuperSimpleForm] Current step: 4 (all complete)');
    return 4;
  }, [dateRange, adventureOptions, adventure.options, customerName, customerEmail, customerPhone, adventure.id]);

  // Simple date selection handler
  const handleDateSelect = (range: any) => {
    console.log('📅 [SuperSimpleForm] Date selection:', range ? 'Setting dates' : 'Clearing dates');
    
    if (range) {
      setDateRange({ from: range.from, to: range.to });
    }
  };

  // Handle adventure option changes
  const handleOptionChange = (optionLabel: string, value: any) => {
    console.log(`⚙️ [SuperSimpleForm] Option "${optionLabel}" = ${value}`);
    
    updateAdventureOptions((prev: Record<string, any>) => ({
      ...prev,
      [optionLabel]: value
    }));
  };

  // Simple form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!dateRange.from || !dateRange.to || !customerName || !customerEmail || !customerPhone) {
      alert('Please fill in all required fields');
      return;
    }

    if (isSubmitting) {
      console.log('Form submission already in progress');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const bookingRequest: BookingRequest = {
        selectedOptions: adventureOptions,
        totalPrice: currentTotalPrice ?? adventure.basePrice ?? 0,
        dateRange,
        customerDetails: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone,
          specialRequests,
          preferredContactMethod: contactMethod as any
        },
        flexibility: flexibility as any,
        ...(currentPriceBreakdown && currentPriceBreakdown.length > 0 && { priceBreakdown: currentPriceBreakdown })
      };

      await onSubmit(bookingRequest);
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit booking request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Adventure Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl lg:text-2xl">Book: {adventure.name}</CardTitle>
          <p className="text-gray-600">{adventure.description}</p>
          <p className="text-lg font-bold text-green-600">
            From €{adventure.basePrice?.toFixed(2) || '0.00'}
          </p>
        </CardHeader>
      </Card>

      {/* Progress Tracker - Full Width */}
      <BookingProgress currentStep={currentStep} totalSteps={4} />

      {/* Form Content - Let parent handle the layout */}
      <form onSubmit={handleSubmit} className="space-y-6 pb-64 lg:pb-0">
            {/* Step 1: Select Dates */}
            <SimpleDateSelection
              dateRange={dateRange}
              flexibility={flexibility}
              onDateSelect={handleDateSelect}
              onFlexibilityChange={setFlexibility}
            />

            {/* Step 2: Customize Your Adventure */}
            <SimpleAdventureOptions
              options={adventure.options || []}
              values={adventureOptions}
              onChange={handleOptionChange}
            />

            {/* Step 3: Your Details */}
            <SimpleCustomerDetails
              customerName={customerName}
              customerEmail={customerEmail}
              customerPhone={customerPhone}
              specialRequests={specialRequests}
              contactMethod={contactMethod}
              onNameChange={setCustomerName}
              onEmailChange={setCustomerEmail}
              onPhoneChange={setCustomerPhone}
              onSpecialRequestsChange={setSpecialRequests}
              onContactMethodChange={setContactMethod}
            />

            {/* Step 4: Submit */}
        <Card>
          <CardHeader>
            <CardTitle>4. Submit Booking Request</CardTitle>
            <p className="text-sm text-gray-600">
              Your request will be sent to the business for review and approval.
            </p>
          </CardHeader>
          <CardContent>
            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={isSubmitting}
              size="lg"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
